import Typography from 'components/Typography';
import PageHeader from 'components/PageHeader';
import Button from 'components/Button';
import FormGroup from 'components/FormGroup';
import TextField from 'components/TextField';
import ListWrapper from 'components/ListWrapper';
import ClickablePagination from 'components/ClickablePagination';

import {validateEmail} from 'utils/validators';
import useUserForm from './useUserForm';
import UserAddresses from './UserAddresses';
import CardInformation, {
  CardInformationRef,
} from './CardInformation/CardInformation';
import DataTable from 'components/DataTable';
import {createAddress, DataTableSchemaItem, ScriptStatus} from 'types';
import {Fragment, Key, useMemo, useRef, useState} from 'react';
import api from 'api';
import toast from 'react-hot-toast';
import ListTabs from './ListTabs';
import {get} from 'lodash';
import Modal from 'components/Modal';
import styles from './UserForm.module.scss';
import Select from 'components/Select';
import {components} from 'react-select';
import ScriptStatusSelect from 'components/ScriptStatusSelect';
import DateTimePicker from 'components/DateTimePicker';
import ArrowIcon from './UserAddresses/ArrowIcon';
import {addressToString} from 'helpers/address';
import {Controller} from 'react-hook-form';
import {GooglePlaceAutoComplete} from 'components/GooglePlaceAutoComplete';
import MapPreview from 'components/MapPreview';
import Morning from 'pages/DeliveryPlanner/icons/morning';
import Afternoon from 'pages/DeliveryPlanner/icons/afternoon';
import Evening from 'pages/DeliveryPlanner/icons/evening';
import {phoneFormat} from 'helpers/phoneFormat';
import DeleteModal from 'components/DeleteModal';
import classNames from 'classnames';
import CreateAddressModal from './CreateAddressModal';
import FilterIcon from 'pages/Requests/FilterIcon';
import OrdersFilterModal from './OrdersFilterModal';
import PriorityFlag from 'components/PriorityFlag';
import CheckIcon from 'pages/Requests/CheckIcon';
import {formatPrice} from 'utils/format-price';
import {CLIENT_ROLES_OPTIONS} from 'constants/select-options';
import FeatureGuard from 'components/FeatureGuard';
import Spinner from 'components/Spinner';
import CustomerTimeline from 'components/CustomerTimeline';

const SCHEMA: DataTableSchemaItem[] = [
  {
    dataKey: 'checkbox',
    header: <div style={{height: 40}}></div>,
    sortable: false,
    colWidth: '80px',
  },
  {
    dataKey: 'number',
    header: 'Order #',
    sortable: true,
    index: 0,
  },
  {
    dataKey: 'customer',
    header: 'Customer',
    sortable: true,
    index: 1,
  },
  {dataKey: 'status', header: 'Status', sortable: true, index: 2},
  {dataKey: 'amount_due', header: 'Amount', sortable: false, index: 4},

  {
    dataKey: 'sync_time',
    header: 'Date/Time',
    sortable: true,
    align: 'center',
    index: 7,
  },
  {
    dataKey: 'priority',
    header: 'Priority',
    sortable: true,
    align: 'center',
    index: 7,
  },

  {
    dataKey: 'preference_date',
    header: 'Preference Date',
    sortable: false,
    index: 8,
  },
  {
    dataKey: 'payment_method',
    header: 'Payment Method',
    sortable: false,
    index: 5,
  },
];

function UserForm() {
  const {
    userId,
    isVendor,
    addresses,
    setAddressesForUpdate,
    submitting,
    errors,
    register,
    handleSubmit,
    orders,
    transactions,
    isLoading,
    refetch,
    totalPages,
    currentPage,
    setCurrentPage,
    status,
    setStatus,
    cards,
    filledOrders,
    copayItems,
    handleNavigateRequest,
    handleNavigateTransaction,
    accessCardModal,
    addressModalVisible,
    cardId,
    selectCardModal,
    setAccessCardModal,
    setCardIs,
    setAddressModalVisible,
    setSelectCardModal,
    selectedOrders,
    showStatusModal,
    setShowStatusModal,
    onChangeStatus,
    data,
    setData,
    deliveryAddressId,
    setDeliveryAddressId,
    openAddressModal,
    setOpenAddressModal,
    addressControl,
    setAddressValee,
    handleAddressSubmit,
    onSelectPlace,
    locationPreview,
    handleAddressCreation,
    orderStatus,
    submitOrderStatutes,
    deleteModal,
    setDeleteModal,
    handleDeleteCustomer,
    tempFilters,
    setTempFilters,
    applyFilter,
    resetFilter,
    landlordSelectOptions,
    propertyAddressOptions,
    propertyNameOptions,
    onSort,
    sortBy,
    sortOrder,
    allSelected,
    setAllSelected,
    setCopayItems,
    isServiceProvider,
    isPropertyManager,
    control,
    filtered,
    timeline,
  } = useUserForm();

  const isDetails = useRef(true);
  const [addressType, setAddressType] =
    useState<createAddress['type']>('dispatch');
  const [filterVisible, setFilterVisible] = useState(false);
  const [isCharging, setIsCharging] = useState(false);

  const cardInformationRef = useRef<CardInformationRef>(null);
  const tabs = useMemo(function () {
    return [
      {title: 'Details', value: ''},
      {title: 'Addresses', value: 'address'},
      {title: 'Cards', value: 'cards'},
      {title: 'Orders', value: 'orders'},
      {title: 'Transactions', value: 'transactions'},
    ];
  }, []);
  const COPAY_SCHEMA: DataTableSchemaItem[] = useMemo(
    () => [
      {
        dataKey: 'checkbox',
        header:
          copayItems.length > 0 ? (
            <div
              onClick={(val) => {
                val.stopPropagation();
              }}
              className={styles.selectAll}
            >
              <div
                onClick={(val) => {
                  setAllSelected(false);
                  setCopayItems([]);
                }}
                className={styles.selectAllIcon}
              >
                <CheckIcon emptySelect={false} allSelected={allSelected} />
              </div>
              <Select
                components={{
                  Input: () => null,
                  ValueContainer: () => null,
                }}
                styles={{
                  control: (base) => ({
                    ...base,
                    border: 'none',
                    backgroundColor: 'transparent',
                  }),
                  container: (base) => ({
                    ...base,
                    width: 70,
                  }),
                }}
                options={[
                  {label: 'All', value: 'all'},
                  {label: 'None', value: 'none'},
                ]}
                onChange={(val) => {
                  if (val?.value === 'all') {
                    setCopayItems(
                      filledOrders?.map((item) => ({
                        id: item.id,
                        amount_due: item.amount_due,
                      }))
                    );
                    setAllSelected(true);
                  } else {
                    setCopayItems([]);
                    setAllSelected(false);
                  }
                }}
              />
            </div>
          ) : (
            <div style={{height: 40}}></div>
          ),
        sortable: false,
        colWidth: '40px',
      },
      {dataKey: 'drug_name', header: 'Offering', sortable: false},
      {
        dataKey: 'status',
        header: 'Status',
        sortable: false,
        align: 'center',
      },
      {
        dataKey: 'amount_due',
        header: 'Amount due',
        sortable: false,
        align: 'center',
      },
    ],
    [
      allSelected,
      copayItems.length,
      filledOrders,
      setAllSelected,
      setCopayItems,
    ]
  );
  const TRANSACTIONS_SCHEME: DataTableSchemaItem[] = useMemo(() => {
    const defaultScheme: DataTableSchemaItem[] = [
      {
        dataKey: 'transaction_id',
        header: 'ID',
        sortable: true,
        index: 0,
      },
      {dataKey: 'amount', header: 'Amount', sortable: true, index: 1},
      {
        dataKey: 'transaction_date',
        header: 'Date',
        sortable: true,
        align: 'center',
        index: 2,
      },
      {
        dataKey: 'transaction_origin',
        header: 'Source',
        align: 'center',
        sortable: false,
        index: 3,
      },
      {
        dataKey: 'list_orders',
        header: 'List of Orders',
        sortable: false,
        index: 6,
      },
      {dataKey: 'card_type', header: 'Card Type', sortable: true, index: 4},
      {
        dataKey: 'payment_method',
        header: 'Payment Method',
        sortable: false,
        colWidth: '200px',
        index: 7,
      },
    ];
    if (isServiceProvider || isPropertyManager) {
      defaultScheme.push({
        dataKey: 'transaction_type',
        header: 'Transaction Type',
        sortable: false,
        colWidth: '200px',
        index: 6,
      });
    }
    return defaultScheme;
  }, [isPropertyManager, isServiceProvider]);
  const onCharge = async () => {
    // if (cards.length < 1) {
    //   toast.error('Please add billing card to user first');
    //   return;
    // }
    // if (cards.length > 1) {
    //   setSelectCardModal(true);
    //   return;
    // }
    setAccessCardModal(true);
  };
  const handleOpenAddressModal = (addressType: createAddress['type']) => {
    setAddressType(addressType);
    setAddressModalVisible(true);
  };
  const submitSelectedCard = async () => {
    try {
      setIsCharging(true);
      await api.post('/payment/charge-all/', {
        customer: userId,
        payment_method_uuid: cardId?.value,
        copay_items: copayItems,
      });
      setAccessCardModal(false);
      toast.success('Successfully charged');
    } catch (error) {
      const message = get(error, 'response.data.detail', 'Error try again');
      toast.error(message);
    } finally {
      setIsCharging(false);
    }
  };
  const getTitle = () => {
    if (userId) {
      return isVendor ? 'Edit Vendor' : 'Edit Customer';
    }
    return isVendor ? 'New Vendor' : 'New Customer';
  };

  if (isLoading) {
    return (
      <div className={styles.loadingContainer}>
        <Spinner />
      </div>
    );
  }

  return (
    <div style={{position: 'relative'}}>
      <form onSubmit={handleSubmit}>
        <PageHeader style={{height: 50}}>
          <Typography variant="h2">{getTitle()}</Typography>
          {status === 'orders' && (
            <div style={{display: 'flex', gap: 10}}>
              {selectedOrders.length > 0 && (
                <Button onClick={() => setShowStatusModal(true)} color="blue">
                  Bulk Update
                </Button>
              )}

              <Button onClick={onCharge}>Charge Unpaid Orders</Button>
            </div>
          )}
        </PageHeader>
        <ListTabs onChange={setStatus} activeTabValue={status} tabs={tabs} />
        <FormGroup style={{marginBottom: 20}} columns={2}>
          {status === '' && (
            <Fragment>
              <ListWrapper className={styles.detailsWrapper}>
                <Fragment>
                  <Typography variant="h3">Details</Typography>
                  <FormGroup columns={2}>
                    <TextField
                      label="First Name"
                      placeholder="First Name"
                      {...register('first_name', {required: 'Required'})}
                      error={errors.first_name?.message}
                    />
                    <TextField
                      label="Last Name"
                      placeholder="Last Name"
                      {...register('last_name', {required: 'Required'})}
                      error={errors.last_name?.message}
                    />
                  </FormGroup>

                  <FormGroup columns={1}>
                    <TextField
                      label="Email Address"
                      placeholder="Email Address"
                      {...register('email', {
                        required: 'Required',
                        validate: validateEmail,
                      })}
                      error={errors.email?.message}
                    />
                  </FormGroup>

                  <FormGroup columns={1}>
                    <TextField
                      label="Phone 1 (main)"
                      placeholder="(123) 456-7890"
                      {...register('phone', {
                        required: 'Required',
                        minLength: {
                          value: 14,
                          message: 'Enter a valid phone number',
                        },
                      })}
                      type="tel"
                      onChange={(e) => {
                        let input = e.target.value.replace(/\D/g, ''); // Faqat raqamlarni qoldirish

                        e.target.value = phoneFormat(input);
                      }}
                      error={errors.phone?.message}
                    />
                  </FormGroup>
                  <FormGroup columns={1}>
                    <TextField
                      label="Phone 2"
                      placeholder="(123) 456-7890"
                      {...register('phone_2', {
                        minLength: {
                          value: 14,
                          message: 'Enter a valid phone number',
                        },
                      })}
                      type="tel"
                      onChange={(e) => {
                        let input = e.target.value.replace(/\D/g, ''); // Faqat raqamlarni qoldirish

                        e.target.value = phoneFormat(input);
                      }}
                      error={errors.phone_2?.message}
                    />
                  </FormGroup>
                  <FormGroup columns={1}>
                    <TextField
                      label="Phone 3"
                      placeholder="(123) 456-7890"
                      {...register('phone_3', {
                        minLength: {
                          value: 14,
                          message: 'Enter a valid phone number',
                        },
                      })}
                      type="tel"
                      onChange={(e) => {
                        let input = e.target.value.replace(/\D/g, ''); // Faqat raqamlarni qoldirish

                        e.target.value = phoneFormat(input);
                      }}
                      error={errors.phone_3?.message}
                    />
                  </FormGroup>
                  <FeatureGuard feature="hasClientRoleFilterOnCustomers">
                    <FormGroup columns={1}>
                      <div className={styles.clientRoleContainer}>
                        <Typography variant="label">Client Role</Typography>
                        <Controller
                          name="client_role"
                          control={control}
                          rules={{required: true}}
                          render={({field}) => (
                            <Select
                              isSearchable={false}
                              menuPosition="fixed"
                              onChange={field.onChange}
                              options={CLIENT_ROLES_OPTIONS}
                              value={field.value}
                              placeholder={
                                <span className={styles.placeholder}>
                                  Client Role
                                </span>
                              }
                              isClearable
                            />
                          )}
                        />
                      </div>
                    </FormGroup>
                  </FeatureGuard>
                  <FormGroup columns={1}>
                    <TextField
                      label="Company"
                      placeholder="Company"
                      {...register('customer_company')}
                      error={errors.customer_company?.message}
                    />
                  </FormGroup>
                </Fragment>
                <div className={styles.buttonsRow}>
                  <Button
                    color="danger"
                    className={styles.save}
                    onClick={() => setDeleteModal(true)}
                  >
                    Delete
                  </Button>
                  <Button
                    className={styles.save}
                    type="submit"
                    onClick={() => {
                      isDetails.current = true;
                    }}
                    loading={isDetails.current && submitting}
                  >
                    Save
                  </Button>
                </div>
              </ListWrapper>
              <CustomerTimeline
                customer_id={userId}
                notes={timeline}
                refetch={refetch}
              />
            </Fragment>
          )}
          {status === 'cards' && (
            <ListWrapper style={{padding: 0}}>
              <CardInformation
                setAddressModalVisible={() => handleOpenAddressModal('billing')}
                addresses={addresses}
                userId={userId}
                ref={cardInformationRef}
              />
            </ListWrapper>
          )}

          <UserAddresses
            setAddressModalVisible={() => handleOpenAddressModal('dispatch')}
            addresses={addresses}
            onSave={() => {
              isDetails.current = false;
            }}
            userId={Number(userId)}
            refetch={refetch}
            isShow={status === 'address'}
            deleting={!isDetails.current && submitting}
            onChange={setAddressesForUpdate}
          />
          <CreateAddressModal
            handleClose={() => setAddressModalVisible(false)}
            onSuccess={() => refetch()}
            customer_id={Number(userId)}
            isOpen={addressModalVisible}
            newAddressType={addressType}
          />
        </FormGroup>
      </form>

      {status === 'orders' && (
        <Fragment>
          <div className={styles.flex}>
            <div
              className={classNames(
                styles.filterBtn,
                filtered && styles.filterBtnActive
              )}
              onClick={() => setFilterVisible(true)}
            >
              <FilterIcon isFilled={filtered} />
              <span>{'Filter'}</span>
            </div>
          </div>
          <DataTable
            schema={SCHEMA}
            data={orders?.map((item) => ({
              ...item,
              amount_due: formatPrice(item.amount_due || 0),
            }))}
            onClick={handleNavigateRequest}
            loading={isLoading}
          />
        </Fragment>
      )}
      {status === 'transactions' && (
        <DataTable
          schema={TRANSACTIONS_SCHEME}
          data={transactions}
          onSort={onSort}
          sortBy={sortBy}
          sortOrder={sortOrder}
          onClick={handleNavigateTransaction}
          loading={isLoading}
        />
      )}
      {(status === 'transactions' || status === 'orders') && totalPages > 1 && (
        <ClickablePagination
          page={currentPage}
          total={totalPages}
          setCurrentPage={setCurrentPage}
        />
      )}
      <DeleteModal
        openModal={deleteModal}
        setOpenModal={setDeleteModal}
        onDelete={() => handleDeleteCustomer(+userId)}
        title="Confirm deleting this customer ?"
      />
      <OrdersFilterModal
        isOpen={filterVisible}
        onFilterModalClose={() => setFilterVisible(false)}
        tempFilters={tempFilters}
        setTempFilters={setTempFilters}
        applyFilter={applyFilter}
        resetFilter={resetFilter}
        landlordSelectOptions={landlordSelectOptions}
        propertyAddressOptions={propertyAddressOptions}
        propertyNameOptions={propertyNameOptions}
      />
      <Modal
        isOpen={selectCardModal}
        onClose={() => setSelectCardModal(false)}
        className={styles.list_wrapper}
      >
        <Typography component="p" className={styles.modalDesc} variant="h2">
          Select Card to Charge:
        </Typography>
        <div className={styles.optionWrapper}>
          {cards.map((item: any, index: Key | null | undefined) => {
            return (
              <label key={index} className={styles.radioOption}>
                <input
                  type="radio"
                  name="radio"
                  defaultChecked={cardId === item.custom_uuid}
                  onClick={() => setCardIs(item.custom_uuid)}
                />
                {/* <div className={styles.column}> */}
                <span className={styles.radioButtonText}>
                  {get(item, 'cardholder_name', '')}
                </span>
                <p
                  style={{marginLeft: 'auto'}}
                  className={styles.radioButtonText}
                >
                  **** **** **** {get(item, 'last4', '')}
                </p>
                {/* </div> */}
              </label>
            );
          })}
        </div>
        <div className={styles.actions}>
          <Button onClick={() => setAccessCardModal(true)} size="md">
            Submit
          </Button>
        </div>
      </Modal>
      <Modal
        isOpen={accessCardModal}
        onClose={() => setAccessCardModal(false)}
        className={styles.orders_wrapper}
      >
        <Typography component="p" className={styles.modalDesc} variant="h2">
          Are you sure you want to charge?
        </Typography>
        <Typography
          component="p"
          style={{marginTop: 25, marginBottom: 10}}
          variant="h4"
        >
          Card:
        </Typography>
        <Select
          className={styles.selectCon}
          isSearchable={false}
          onChange={(val: any) => setCardIs(val)}
          options={cards.map((item) => ({
            value: item.custom_uuid,
            label: `**** **** **** ${get(item, 'last4', '')} - ${
              item.cardholder_name
            }`,
          }))}
          value={cardId}
          placeholder={
            <span className="select-placeholder-text">Select Card</span>
          }
          components={{
            NoOptionsMessage: (props) => {
              return (
                <components.NoOptionsMessage {...props}>
                  No available cards
                  <div
                    onClick={() => {
                      setStatus('cards');
                      setAccessCardModal(false);
                      setTimeout(() => {
                        cardInformationRef.current?.addCard();
                      }, 300);
                    }}
                    className={styles.addCard}
                  >
                    + Add Card
                  </div>
                </components.NoOptionsMessage>
              );
            },
          }}
        />

        <div className={styles.totalContainer}>
          <Typography component="p" style={{marginTop: 25}} variant="h4">
            Orders:
          </Typography>
          <Typography component="p" style={{marginTop: 25}} variant="h4">
            Total: $
            {copayItems
              .reduce(
                (pr: number, curr: any) => pr + Number(curr.amount_due),
                0
              )
              .toFixed(2)}
          </Typography>
        </div>

        <DataTable
          containerStyle={styles.orders}
          wrapperStyle={styles.ordersWrapper}
          schema={COPAY_SCHEMA}
          data={filledOrders?.map((item) => ({
            ...item,
            amount_due: formatPrice(item.amount_due || 0),
          }))}
          onClick={handleNavigateRequest}
        />
        <div className={styles.actions}>
          <Button
            type="submit"
            disabled={!cardId?.value || copayItems.length === 0 || isCharging}
            onClick={submitSelectedCard}
            size="md"
            loading={isCharging}
          >
            Submit
          </Button>
        </div>
      </Modal>
      <Modal
        isOpen={showStatusModal}
        onClose={() => setShowStatusModal(false)}
        className={styles.status_wrapper}
      >
        <div className={styles.times}>
          <div style={{flex: 1}}>
            <div className={styles.label}>Status:</div>
            <ScriptStatusSelect
              name={'status'}
              status={orderStatus as ScriptStatus}
              onChange={onChangeStatus}
              type="prescription"
              origin={'micromerchant'}
              isPvfr={true}
              menuPosition="fixed"
              classNamePrefix={'order_select'}
              className={orderStatus || 'default_selected'}
            />
          </div>
          <div style={{flex: 1}}>
            <div className={styles.label}>Priority:</div>
            <Select
              placeholder="Select Priority"
              onChange={(data) => {
                setData((pr) => ({
                  ...pr,
                  priority: String(data?.value || ''),
                }));
              }}
              defaultValue={
                data.priority
                  ? {
                      value: data.priority,
                      label:
                        data.priority[0].toUpperCase() + data.priority.slice(1),
                    }
                  : undefined
              }
              className={styles.priority}
              options={[
                {
                  label: 'High',
                  value: 'high',
                },
                {
                  label: 'Medium',
                  value: 'medium',
                },
                {
                  label: 'Low',
                  value: 'low',
                },
              ]}
              isClearable
              components={{
                DropdownIndicator: (props) =>
                  !data.priority ? (
                    <components.DropdownIndicator {...props} />
                  ) : null,
                Option: (props) => {
                  return (
                    <components.Option
                      {...props}
                      className={styles.select_option}
                    >
                      {props.data.label}
                      <PriorityFlag priority={props.data.value as any} />
                    </components.Option>
                  );
                },
              }}
            />
          </div>
        </div>
        <div className={styles.times}>
          <div style={{flex: 1}}>
            <div className={styles.label}>Preference Date:</div>
            <div className={styles.calendarRoot}>
              <DateTimePicker
                showTimeSelect={false}
                dateFormat="MM/dd/yy E"
                value={data.preference_date ? data.preference_date : undefined}
                onChange={(date) => {
                  setData((pr) => ({
                    ...pr,
                    preference_date: date ? date : null,
                  }));
                }}
              />
            </div>
          </div>
          <div style={{flex: 1}}>
            <div className={styles.label}>Preference Time:</div>
            <Select
              className={styles.select}
              onChange={(time) => {
                setData((pr) => ({
                  ...pr,
                  preference_time: String(time?.value || ''),
                }));
              }}
              placeholder={
                <span className="select-placeholder-text">Select Time</span>
              }
              isClearable
              components={{
                DropdownIndicator: (props) =>
                  !data.preference_time ? (
                    <components.DropdownIndicator {...props} />
                  ) : null,
                Option: (props) => {
                  const icons = {
                    morning: <Morning />,
                    afternoon: <Afternoon />,
                    evening: <Evening />,
                  };
                  return (
                    <components.Option
                      {...props}
                      className={styles.select_option}
                    >
                      {props.data.label}
                      {icons[props.data.value as keyof typeof icons]}
                    </components.Option>
                  );
                },
              }}
              value={
                data.preference_time
                  ? {
                      value: data.preference_time,
                      label:
                        data.preference_time[0].toUpperCase() +
                        data.preference_time.slice(1),
                    }
                  : null
              }
              options={[
                {value: 'morning', label: 'Morning'},
                {value: 'afternoon', label: 'Afternoon'},
                {value: 'evening', label: 'Evening'},
              ]}
              size="md"
            />
          </div>
        </div>
        <div className={styles.label}> Select Delivery Address:</div>
        <div className={styles.optionWrapperAddress}>
          {addresses.map((item: any, index: Key | null | undefined) => {
            return (
              <label key={index} className={styles.containerAddress}>
                <input
                  type="radio"
                  name="radio"
                  defaultChecked={deliveryAddressId === item.id}
                  onClick={() => setDeliveryAddressId(item.id)}
                />
                <span className={styles.radioButtonText}>
                  {addressToString(item)}
                </span>
                {!get(item, 'is_valid', true) && (
                  <span className={styles.wrongAddress}>issue</span>
                )}
              </label>
            );
          })}
          <div
            onClick={() => setOpenAddressModal((pr) => !pr)}
            className={styles.addAddress}
          >
            <p>+ Add Address</p>
            <ArrowIcon
              className={
                openAddressModal ? styles.icon_rotated : styles.arror_icon
              }
            />
          </div>
          {openAddressModal && (
            <ListWrapper
              style={{
                marginTop: 10,
                paddingLeft: 20,
                paddingRight: 20,
                paddingBottom: 15,
              }}
            >
              <FormGroup>
                <Controller
                  name="city"
                  control={addressControl}
                  rules={{required: 'Please, Select the Address'}}
                  render={({field, fieldState}) => (
                    <GooglePlaceAutoComplete
                      label="Your Address"
                      placeholder="Address"
                      onPlaceSelected={onSelectPlace}
                      error={fieldState.error?.message}
                      options={{
                        types: ['address'],
                      }}
                    />
                  )}
                />
                <Controller
                  name="unit_address"
                  control={addressControl}
                  render={({field, fieldState}) => (
                    <TextField
                      label="Unit Number"
                      wrapperClass={styles.googleInput}
                      placeholder="Enter Unit Number"
                      hasError={false}
                      onChange={field.onChange}
                      error={fieldState.error?.message}
                    />
                  )}
                />
              </FormGroup>
              <div
                style={{
                  alignItems: 'center',
                  display: 'flex',
                  flexDirection: 'column',
                  paddingTop: 20,
                }}
              >
                {!!locationPreview.latitude && (
                  <MapPreview
                    zoom={15}
                    onDrag={(location) => {
                      setAddressValee('latitude', location.lat);
                      setAddressValee('longitude', location.lon);
                    }}
                    center={[
                      locationPreview.latitude,
                      locationPreview.longitude,
                    ]}
                  />
                )}
              </div>

              <div className={styles.actionWrapper}>
                <Button
                  type="submit"
                  onClick={handleAddressSubmit(handleAddressCreation)}
                  color="primary"
                >
                  Add
                </Button>
              </div>
            </ListWrapper>
          )}
        </div>
        <div className={styles.actions}>
          <Button
            type="submit"
            color="primary"
            disabled={!orderStatus || !deliveryAddressId}
            onClick={submitOrderStatutes}
            size="md"
          >
            Submit
          </Button>
        </div>
      </Modal>
    </div>
  );
}

export default UserForm;
